import isEmpty from 'lodash/isEmpty';
import { Fragment, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import type {
  AccountApiError,
  AccountApiResponse,
} from '@/types/accountApiType';
import type { SalesforceLanguageCodeEnum } from '@/types/regionTypes';

import { Typography } from '@/components/Typography';
import type { CountryCode } from '@/graphql/saleor/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import type { NewsLetterSubscription } from '@/hooks/useNewsLetter';
import { useNewsLetter } from '@/hooks/useNewsLetter';
import { useUser } from '@/hooks/useUser';
import { getMetaValue } from '@/lib/address';
import { Brand, getBrandConfig } from '@/lib/configuration/configuration';
import { BRAND } from '@/lib/const';
import { EVENT_NAME, popinTitle } from '@/lib/gtm/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getLocaleForCountryAndLanguage } from '@/lib/regions';
import * as M from '@/messages/newsletter';
import { userState } from '@/states/user';
import { theme } from '@/theme/getTheme';
import { NewsLetterForm } from '@/views/NewsLetterView/NewsLetterForm';
import type { NewsLetterSchema } from '@/views/NewsLetterView/schema';

import { Box } from '../Box';
import type { LoaderVariant } from '../Loader';
import { Loader } from '../Loader';
import { Logo } from '../Logo';
import { Modal } from '../Modal';
import type { SelectOption } from '../Select';
import { Visibility } from '../Visibility';

import * as S from './NewsLetterModal.styles';

export interface NewsLetterModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

export const NewsLetterModal = ({
  children,
  isOpen,
  onClose,
}: NewsLetterModalProps) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<AccountApiError | undefined>(undefined);
  const {
    getNewsLetterSubscriptionStatus,
    subscribeNewsLetterForGuest,
    subscribeNewsLetterForUser,
  } = useNewsLetter();
  const [loading, setLoading] = useState<boolean>(false);
  const [userMetadataLanguage, setUserMetadataLanguage] = useState<
    string | null
  >(null);

  const {
    snapshot: { user },
  } = useUser();

  useEffect(() => {
    if (user?.metadata) {
      const getUserMetadataLanguage = getMetaValue(
        ACCOUNT_METADATA.language,
        user.metadata,
      );
      setUserMetadataLanguage(getUserMetadataLanguage);
    }
  }, [user?.metadata]);

  const initData = async () => {
    try {
      setLoading(true);
      const res = await getNewsLetterSubscriptionStatus();
      setLoading(false);
      if (res.code === 200 && res.data) {
        setIsSubscribed(res.data.activeSubscription);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleSubmit: SubmitHandler<NewsLetterSchema> = async ({
    country,
    email,
    firstName,
    firstNameKanji,
    language,
    lastName,
    lastNameKanji,
    newsletterConsent,
    phone,
    phonePrefix,
    reCaptchaResponse,
    title,
  }: NewsLetterSchema) => {
    if (!newsletterConsent) return;
    const _country = country as SelectOption;
    const _language = language as SelectOption;
    const _title = title as SelectOption | null;
    const languageCode = _language.value as SalesforceLanguageCodeEnum;
    const reCaptchaBackgroundValidation =
      getBrandConfig().newsletter?.reCaptchaBackgroundValidation;

    try {
      let res: AccountApiResponse<NewsLetterSubscription>;
      setLoading(true);
      if (userState.isAuthenticated) {
        res = await subscribeNewsLetterForUser({
          websiteCountryCode: _country.value as CountryCode,
          languageCode: userMetadataLanguage || languageCode, //userMetadataLanguage may be ''
        });
      } else {
        res = await subscribeNewsLetterForGuest({
          countryLanguageSlug: getLocaleForCountryAndLanguage(
            _country.value,
            languageCode,
          ),
          websiteCountryCode: _country.value as CountryCode,
          languageCode,
          title: _title?.value ?? '',
          firstName,
          lastName,
          email,
          ...(!!firstNameKanji && { firstNameKanji }),
          ...(!!lastNameKanji && { lastNameKanji }),
          ...(!!phone && { phone }),
          ...(!!phonePrefix?.value && { phonePrefix: phonePrefix?.value }),
          ...(!!reCaptchaBackgroundValidation && {
            recaptchaResponse: reCaptchaResponse,
          }),
        });
      }
      setLoading(false);
      if (res.code === 200) {
        sendEvent({
          event: EVENT_NAME.NEWSLETTER_SUBSCRIPTION,
          newsletter_language: _language.value.toLocaleLowerCase(),
          newsletter_country: _country.value,
          opt_in: newsletterConsent ? 'yes' : 'no',
        });
        setIsSubscribed(true);
        setError(undefined);
      } else {
        setError({
          code: res.code,
          msg: res.msg ?? res.message ?? res?.detail?.[0]?.msg,
        });
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) setIsSubscribed(false);
  }, [isOpen]);

  useEffect(() => {
    if (userState.isAuthenticated) initData();
  }, [userState.isAuthenticated, isOpen]);

  const { sendEvent } = useGoogleAnalytics();

  useEffect(() => {
    if (isOpen) {
      sendEvent({
        event: EVENT_NAME.POPIN_VIEW,
        popin_title: popinTitle.newsletter,
      });
    }
  }, [isOpen]);

  const handleModalClose = () => {
    sendEvent({
      event: EVENT_NAME.POPIN_CLOSE,
      popin_title: popinTitle.newsletter,
    });

    onClose();
  };

  const newsletterTitle =
    (BRAND === Brand.Gallet && !isSubscribed) || BRAND === Brand.Breitling
      ? M.NewsletterTitle
      : '\u00A0';

  return (
    <S.Modal
      data-test-id='NewsLetterModal'
      gutters={false}
      isOpen={isOpen}
      maxWidth='sm'
      onClose={handleModalClose}
    >
      <Modal.Header
        data-test-id='NewsLetterModalHeader'
        divider={
          !isSubscribed
            ? true
            : !theme.components.newsLetter.subscribedHeaderDividerHidden
        }
        onClose={handleModalClose}
      >
        <S.Title>{newsletterTitle}</S.Title>
      </Modal.Header>
      <S.ModalBody data-test-id='NewsLetterModalBody'>
        {loading && (
          <Loader
            data-test-id='NewsLetterModalLoader'
            variant={theme.components.newsLetter.loaderVariant as LoaderVariant}
          />
        )}
        <Visibility handleDisplay={true} xsHidden={loading}>
          <Box>
            {!isSubscribed ? (
              <Fragment>
                {children}
                <NewsLetterForm
                  data-test-id='NewsLetterModalForm'
                  handleSubmit={handleSubmit}
                  isProcessing={loading}
                  source='modal'
                ></NewsLetterForm>
              </Fragment>
            ) : (
              <S.SuccessMessage data-test-id='NewsLetterModalSuccessMessage'>
                {userState.isAuthenticated
                  ? M.NewsLetterSubSuccessDescUser
                  : M.NewsLetterSubSuccessDescGuest}
              </S.SuccessMessage>
            )}
            {!isEmpty(error) && error.msg && (
              <Typography
                color='error'
                data-test-id='NewsLetterModalErrorMessage'
                scale='p3'
                textAlign='center'
              >
                {error.msg}
              </Typography>
            )}
          </Box>
        </Visibility>
      </S.ModalBody>
      <S.ModalTail>
        <S.ModalTailButton
          aria-label='Close newsletter modal'
          onClick={() => handleModalClose()}
          onKeyDown={(e) => e.key === 'Enter' && handleModalClose()}
          tabIndex={0}
        >
          <Logo disableClick={false} variant='logoLine' />
        </S.ModalTailButton>
      </S.ModalTail>
    </S.Modal>
  );
};

NewsLetterModal.displayName = 'NewsLetterModal';
